import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, ShieldAlert } from "lucide-react";
import { employeeApi } from "../../services/employeeApi";
import { StatusBadge } from "../../components/StatusBadge";
import { EagleState } from "../../components/EagleState";
import { EmployeeAvatar } from "../../components/EmployeeAvatar";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { AccessDenied } from "../../components/AccessDenied";
import { useRouteRefresh } from "../../hooks/useRouteRefresh";
import { usePermissions } from "../../hooks/usePermissions";
import { WORK_STATUSES } from "../../lib/constants";
import { useLocalization } from "../../hooks/useLocalization";

export default function EmployeeDirectoryPage() {
  const { t } = useLocalization();
  const perm = usePermissions();
  const [employees, setEmployees] = useState([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  const load = useCallback(() => {
    employeeApi.list()
      .then((r) => { setEmployees(r.data); setAccessDenied(false); })
      .catch((err) => { if (err.response?.status === 403) setAccessDenied(true); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useRouteRefresh(load);

  const filtered = employees
    .filter((e) => statusFilter === "ALL" || (e.work_status || "ACTIVE") === statusFilter)
    .filter((e) => `${e.legal_name} ${e.employee_id} ${e.position}`.toLowerCase().includes(query.toLowerCase()));

  if (accessDenied) return <AccessDenied resourceLabel={t("employees.title")} />;

  return (
    <div data-testid="employee-directory-page">
      <PageHelpBanner pageKey="employee-directory" title="How to use this page">
        <p>This is every employee in the company, active or not. Click any row to open that person's full profile - attendance, payroll, leave, training, and documents all live there. Use the filters below to narrow by work status, and search by name, ID, or position.</p>
      </PageHelpBanner>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("employees.title")}</h2>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
            <input
              data-testid="employee-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("common.search")}
              className="pl-9 pr-4 py-2 rounded-full border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <Link
            to="/management/employees/new"
            data-testid="new-employee-button"
            className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 hover:-translate-y-[1px] active:scale-95 transition-all"
          >
            <Plus size={16} /> {t("employees.newEmployee")}
          </Link>
          {perm.canPreviewTestCleanup && (
            <Link
              to="/management/employees/test-data-review"
              data-testid="test-data-review-link"
              className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-red-200 text-red-600 text-sm font-semibold hover:bg-red-50 active:scale-95 transition-all"
            >
              <ShieldAlert size={16} /> {t("testDataReview.title")}
            </Link>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-6">
        {["ALL", ...WORK_STATUSES].map((status) => (
          <button
            key={status}
            data-testid={`employee-filter-${status}`}
            onClick={() => setStatusFilter(status)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all active:scale-95 ${
              statusFilter === status ? "bg-gray-900 text-white border-gray-900" : "border-gray-300 text-gray-600 hover:bg-gray-50"
            }`}
          >
            {status === "ALL" ? t("common.all") : t(`statuses.${status}`)}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              <th className="px-4 py-3 font-medium"></th>
              <th className="px-4 py-3 font-medium">{t("employees.employeeId")}</th>
              <th className="px-4 py-3 font-medium">{t("employees.legalName")}</th>
              <th className="px-4 py-3 font-medium">{t("employees.position")}</th>
              <th className="px-4 py-3 font-medium">{t("employees.storeLocation")}</th>
              <th className="px-4 py-3 font-medium">{t("workStatus.title")}</th>
              <th className="px-4 py-3 font-medium">{t("employees.workClearance")}</th>
            </tr>
          </thead>
          <tbody>
            {!loading && filtered.map((emp) => (
              <tr key={emp.id} className="border-t border-gray-100 hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3"><EmployeeAvatar employee={emp} size={32} /></td>
                <td className="px-4 py-3">
                  <Link to={`/management/employees/${emp.id}`} data-testid={`employee-row-${emp.employee_id}`} className="font-medium text-blue-600 hover:underline">
                    {emp.employee_id}
                  </Link>
                </td>
                <td className="px-4 py-3">{emp.legal_name}</td>
                <td className="px-4 py-3">{emp.position}</td>
                <td className="px-4 py-3">{emp.store_location}</td>
                <td className="px-4 py-3"><StatusBadge status={emp.work_status || "ACTIVE"} /></td>
                <td className="px-4 py-3"><StatusBadge status={emp.work_clearance_status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && filtered.length === 0 && (
          <EagleState variant="empty" title={t("eagle.noEmployeesTitle")} message={t("eagle.noEmployeesMessage")} testId="employee-directory-empty" />
        )}
      </div>
    </div>
  );
}
